import { DollarSign, Loader2, X } from "lucide-react";

/**
 * Collapsible estimate preview shown inside the Messages chat view so the
 * admin can adjust line items without leaving the conversation.
 *
 * Owns no state of its own — the parent (`Messages.js`) holds the editing
 * draft + save status and passes callbacks for each interaction.
 */
export default function InlineEstimatePanel({
  estimate,
  editingLineItems,
  saving,
  formatCurrency,
  onClose,
  onSave,
  onUpdateQty,
  onRemoveItem,
  onOpenFullPage,
}) {
  if (!estimate) return null;

  const subtotal = editingLineItems.reduce(
    (sum, item) => sum + (item.unit_price * (item.quantity || 1)),
    0
  );
  const discount = estimate.estimate_data?.discount_amount || 0;
  const tax = estimate.estimate_data?.tax || 0;
  const total = estimate.estimate_data?.total || 0;
  const statusClass =
    estimate.estimate_status === "converted"
      ? "bg-green-100 text-green-700"
      : estimate.estimate_status === "nurturing"
      ? "bg-purple-100 text-purple-700"
      : "bg-zinc-100 text-zinc-600";

  return (
    <div className="bg-zinc-50 border-b border-zinc-200 px-4 py-3 max-h-[40vh] overflow-y-auto" data-testid="inline-estimate-panel">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-zinc-900 flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-[#207024]" />
            Estimate #{estimate.estimate_number || estimate.lead_id?.slice(-6)}
          </h3>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-medium px-2 py-1 rounded ${statusClass}`}>
              {estimate.estimate_status || "new"}
            </span>
            <button
              data-testid="open-full-estimate-btn"
              onClick={() => onOpenFullPage(estimate.lead_id)}
              className="text-[11px] font-semibold text-zinc-400 hover:text-[#207024] uppercase tracking-wide"
              title="Open full estimate page (send, PDF, more options)"
            >
              Full Page
            </button>
            <button
              data-testid="close-estimate-panel-btn"
              onClick={onClose}
              className="p-1 text-zinc-400 hover:text-zinc-700 rounded"
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Line Items */}
        <div className="space-y-2 mb-3">
          {editingLineItems.map((item, index) => (
            <div key={index} className="flex items-center justify-between bg-white rounded-lg p-2 border">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-zinc-900 truncate">{item.name}</p>
                <p className="text-xs text-zinc-500">{formatCurrency(item.unit_price)} each</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onUpdateQty(index, -1)}
                  className="w-7 h-7 flex items-center justify-center rounded-full bg-zinc-100 hover:bg-zinc-200 text-zinc-600"
                >
                  <span className="text-lg font-bold">−</span>
                </button>
                <span className="w-6 text-center font-semibold text-sm">{item.quantity || 1}</span>
                <button
                  type="button"
                  onClick={() => onUpdateQty(index, 1)}
                  className="w-7 h-7 flex items-center justify-center rounded-full bg-[#207024] hover:bg-[#1a5c1d] text-white"
                >
                  <span className="text-lg font-bold">+</span>
                </button>
                <button
                  type="button"
                  onClick={() => onRemoveItem(index)}
                  className="p-1 text-zinc-400 hover:text-red-500 ml-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Totals */}
        <div className="bg-white rounded-lg p-3 border mb-3">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-zinc-500">Subtotal</span>
            <span>{formatCurrency(subtotal)}</span>
          </div>
          {discount > 0 && (
            <div className="flex justify-between text-sm text-green-600 mb-1">
              <span>Discount</span>
              <span>-{formatCurrency(discount)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm mb-1">
            <span className="text-zinc-500">Tax</span>
            <span>{formatCurrency(tax)}</span>
          </div>
          <div className="flex justify-between font-bold border-t pt-1">
            <span>Total</span>
            <span>{formatCurrency(total)}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 h-9 rounded-lg border border-zinc-300 text-zinc-700 font-medium text-sm hover:bg-zinc-100"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            data-testid="save-inline-estimate-btn"
            className="flex-1 h-9 rounded-lg bg-[#207024] hover:bg-[#1a5c1d] text-white font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}
